/**
 * The WordPress reporter plugin on a Saidiseire site page: the CRM's side of
 * the contract, and the rules the card needs (docs/saidiseire-plaan.md, item F).
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-monitoring/reporter/types.ts`.
 * `monitoringReporter.test.ts` reads that file and compares the field names of
 * every interface, the same arrangement as `monitoring.ts`.
 *
 * React-free: what a finding is called, which tone it wears, what a diff says
 * line by line and how the plugin list is ordered are decided here and tested
 * without mounting anything.
 */
import { ApiError, api } from "./api";
import { MONITOR_SITES_PATH } from "./monitoring";
import { backupVerdict, scheduleKey, verdictAlarm } from "./monitoringBackup";

// ── Contract (mirrors crm-monitoring/reporter/types.ts) ─────────────────────────

export type ReporterFindingLevel = "critical" | "warning" | "info";

export interface ReporterFinding {
  code: string;
  level: ReporterFindingLevel;
  count: number | null;
  detail: string | null;
}

export interface ReporterPluginView {
  file: string;
  slug: string;
  name: string;
  version: string;
  active: boolean;
  update: string | null;
}

export interface ReporterMuPluginView {
  file: string;
  name: string;
  version: string;
}

export interface ReporterThemeView {
  slug: string;
  name: string;
  version: string;
  active: boolean;
  parent: string | null;
  update: string | null;
}

export interface ReporterIntegrityView {
  status: string;
  checked: number;
  modified: string[];
  missing: string[];
  unexpected: string[];
  truncated: boolean;
}

export interface ReporterUploadFileView {
  path: string;
  size: number;
  modifiedAt: string;
  /** False for an empty or comment-only guard file: listed, not a finding. */
  code: boolean;
}

export interface ReporterUploadsView {
  status: string;
  scanned: number;
  files: ReporterUploadFileView[];
  truncated: boolean;
}

export interface ReporterExposureView {
  wpDebug: boolean;
  debugDisplay: boolean;
  debugLog: boolean;
  debugLogBytes: number | null;
  displayErrors: boolean;
  fileEdit: boolean;
  exposedFiles: string[];
}

export interface ReporterAdminsView {
  count: number;
  newLogins: string[];
  baseline: boolean;
}

export interface ReportVersionChange {
  from: string;
  to: string;
}

export interface ReportItemRef {
  id: string;
  name: string;
  version: string;
}

export interface ReportItemUpdate {
  id: string;
  name: string;
  from: string;
  to: string;
}

export interface ReportItemChanges {
  added: ReportItemRef[];
  removed: ReportItemRef[];
  updated: ReportItemUpdate[];
  activated: string[];
  deactivated: string[];
}

export interface ReportPathRef {
  kind: string;
  path: string;
}

export interface ReportDiffView {
  core: ReportVersionChange | null;
  php: ReportVersionChange | null;
  plugins: ReportItemChanges;
  themes: ReportItemChanges;
  muPlugins: { added: string[]; removed: string[] };
  newAdmins: string[];
  adminCount: { from: number; to: number } | null;
  filesAdded: ReportPathRef[];
  filesCleared: ReportPathRef[];
  exposureRaised: string[];
  exposureCleared: string[];
  total: number;
  /** Item J: null when the diff holds no update, absent on a diff stored before item J. */
  backupBefore?: BackupBeforeView | null;
}

/** An update judged against the backups that existed when it happened (docs/saidiseire-varundus.md). */
export interface BackupBeforeView {
  /** full | partial | stale | none | unknown */
  verdict: string;
  changedAt: string;
  dbAt: string | null;
  filesAt: string | null;
}

export interface ReporterReportView {
  id: string;
  receivedAt: string;
  signedAt: string;
  reason: string;
  pluginVersion: string;
  homeUrl: string;
  hostMatches: boolean;
  multisite: boolean;
  locale: string;
  coreVersion: string;
  coreUpdate: string | null;
  phpVersion: string;
  phpEol: boolean;
  plugins: ReporterPluginView[];
  muPlugins: ReporterMuPluginView[];
  themes: ReporterThemeView[];
  admins: ReporterAdminsView;
  integrity: ReporterIntegrityView;
  uploads: ReporterUploadsView;
  exposure: ReporterExposureView;
  findings: ReporterFinding[];
  diff: ReportDiffView | null;
  /** Item J. Null for a report from a plugin before 1.1.0. */
  backup: ReporterBackupView | null;
  database: ReporterDatabaseView | null;
}

export interface ReporterBackupSetView {
  at: string;
  db: boolean;
  files: boolean;
  /** Sent to remote storage, not only kept on the site's own server. */
  remote: boolean;
  parts: string[];
  services: string[];
}

export interface ReporterBackupRunView {
  at: string;
  ok: boolean;
  errors: number;
  parts: string[];
}

export interface ReporterBackupView {
  /** updraftplus | other | none */
  tool: string;
  updraftInactive: boolean;
  otherTools: string[];
  lastDbAt: string | null;
  lastFilesAt: string | null;
  lastRun: ReporterBackupRunView | null;
  /** Newest first, at most 20. */
  sets: ReporterBackupSetView[];
  setCount: number;
  /** Where new backups go. Empty = the site's own server only. */
  services: string[];
  dbEvery: string | null;
  filesEvery: string | null;
  keepDb: number | null;
  keepFiles: number | null;
  nextDbAt: string | null;
  nextFilesAt: string | null;
  /** The newest database backup is older than 36 h, or there is none. */
  dbOverdue: boolean;
}

export interface ReporterCleanupView {
  active: boolean;
  scheduled: boolean;
  /** wpo_daily | wpo_weekly | wpo_fortnightly | wpo_monthly | ... */
  every: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

/** Each count is null when the site could not count it. */
export interface ReporterDatabaseView {
  sizeBytes: number | null;
  tables: number | null;
  autoloadBytes: number | null;
  autoloadCount: number | null;
  revisions: number | null;
  autoDrafts: number | null;
  trashedPosts: number | null;
  spamComments: number | null;
  trashedComments: number | null;
  expiredTransients: number | null;
  junkRows: number | null;
  /** Null when WP-Optimize is not installed. */
  cleanup: ReporterCleanupView | null;
}

/** What the site's package asks of its backups and database; all false without a package. */
export interface ReporterExpectView {
  backupDaily: boolean;
  backupBeforeUpdate: boolean;
  dbCleanup: boolean;
}

export interface ReporterHistoryRow {
  id: string;
  receivedAt: string;
  reason: string;
  coreVersion: string;
  phpVersion: string;
  plugins: number;
  updates: number;
  integrityIssues: number;
  uploadsPhp: number;
  changes: number | null;
  /** Item J: the backup verdict of the update that report holds, null when it holds none. */
  backupBefore: string | null;
}

export interface ReporterLinkView {
  keyHint: string;
  issuedAt: string;
  lastReportAt: string | null;
  stale: boolean;
}

export interface ReporterDetailResponse {
  link: ReporterLinkView | null;
  latest: ReporterReportView | null;
  history: ReporterHistoryRow[];
  /** Item J: what the site's package asks of its backups and database. */
  expect: ReporterExpectView;
}

export interface ReporterTokenResponse {
  token: string;
  link: ReporterLinkView;
}

// ── Routes ───────────────────────────────────────────────────────────────────

export const reporterPath = (monitorId: string) => `${MONITOR_SITES_PATH}/${encodeURIComponent(monitorId)}/reporter`;

export const getReporter = (monitorId: string, signal?: AbortSignal): Promise<ReporterDetailResponse> =>
  api.get<ReporterDetailResponse>(reporterPath(monitorId), signal);

export const issueReporterToken = (monitorId: string): Promise<ReporterTokenResponse> =>
  api.post<ReporterTokenResponse>(`${reporterPath(monitorId)}/token`);

export const revokeReporter = (monitorId: string): Promise<void> => api.del<void>(reporterPath(monitorId));

/**
 * A refused token or revoke request, as a key under `monitoring.reporter.error`.
 * `notInPlan`: the site's web maintenance package does not include the reporter.
 * `platformSite`: the site runs on our own platform, which needs no plugin.
 */
export function reporterErrorKey(err: unknown): "forbidden" | "notFound" | "notInPlan" | "platformSite" | "failed" {
  if (!(err instanceof ApiError)) return "failed";
  const code = (err.body as { error?: unknown } | null)?.error;
  if (code === "not_in_plan") return "notInPlan";
  if (code === "platform_site") return "platformSite";
  if (err.status === 403) return "forbidden";
  if (err.status === 404) return "notFound";
  return "failed";
}

// ── Rules ────────────────────────────────────────────────────────────────────

/**
 * Where the link stands: no token, a token nothing has reported with yet, a
 * link gone quiet (no report for two days, judged by the server), or one that
 * reports.
 */
export type ReporterLinkState = "none" | "waiting" | "silent" | "ok";

export function reporterLinkState(detail: Pick<ReporterDetailResponse, "link" | "latest">): ReporterLinkState {
  const link = detail.link;
  if (!link) return "none";
  if (link.stale) return "silent";
  const reportedSinceToken = link.lastReportAt !== null && link.lastReportAt >= link.issuedAt;
  return reportedSinceToken ? "ok" : "waiting";
}

export function linkTone(state: ReporterLinkState): "ok" | "wt" | "lt" | "mut" {
  if (state === "ok") return "ok";
  if (state === "waiting") return "wt";
  if (state === "silent") return "lt";
  return "mut";
}

export function findingTone(level: ReporterFindingLevel): "lt" | "wt" | "info" {
  if (level === "critical") return "lt";
  if (level === "warning") return "wt";
  return "info";
}

/** The finding codes this CRM has a sentence for (`monitoring.reporter.finding.<code>`). */
export const FINDING_CODES = [
  "host_mismatch",
  "core_modified",
  "core_unexpected",
  "core_missing",
  "integrity_unavailable",
  "integrity_partial",
  "uploads_php",
  "uploads_partial",
  "new_admins",
  "core_update",
  "plugin_updates",
  "theme_updates",
  "php_eol",
  "exposed_files",
  "debug_display",
  "display_errors",
  "debug_log_file",
  "file_edit",
  "inactive_plugins",
  // Item J: backups and the database.
  "backup_none",
  "backup_other",
  "backup_overdue",
  "backup_schedule",
  "backup_failed",
  "backup_local",
  "db_cleanup_off",
  "db_autoload",
  "db_junk",
  "reporter_outdated",
] as const;

/**
 * The sentence of one finding: `monitoring.reporter.finding.<code>` with the
 * count as `{n}` and `{detail}`. Three item J findings read differently:
 * `backup_overdue` without a count means there is no database backup at all,
 * `backup_schedule` names its schedule in words (`detailKey`, the raw slug when
 * this CRM does not know it), and `backup_failed` failed at least once even
 * when UpdraftPlus counted no error.
 */
export function findingMessage(f: ReporterFinding): { key: string; vars: { n: number; detail: string }; detailKey: string | null } {
  const vars = { n: f.count ?? 0, detail: f.detail ?? "" };
  const key = `monitoring.reporter.finding.${f.code}`;
  if (f.code === "backup_overdue" && f.count === null) return { key: `${key}_never`, vars, detailKey: null };
  if (f.code === "backup_schedule") return { key, vars, detailKey: scheduleKey(f.detail) };
  if (f.code === "backup_failed") return { key, vars: { ...vars, n: Math.max(1, f.count ?? 1) }, detailKey: null };
  return { key, vars, detailKey: null };
}

/** Plugins with an update first, then the active ones, then by name. */
export function orderPlugins(plugins: readonly ReporterPluginView[]): ReporterPluginView[] {
  return [...plugins].sort(
    (a, b) =>
      Number(Boolean(b.update)) - Number(Boolean(a.update)) ||
      Number(b.active) - Number(a.active) ||
      (a.name || a.slug).localeCompare(b.name || b.slug),
  );
}

/** One line of "what changed since the report before", as a catalogue key and its values. */
export interface DiffLine {
  key: string;
  vars: Record<string, string | number>;
  /** A line a person should look at before the others (a new admin, a new file finding). */
  alarm: boolean;
}

const EXPOSURE_KEYS: Record<string, string> = {
  debugDisplay: "debugDisplay",
  displayErrors: "displayErrors",
  debugLogFile: "debugLogFile",
  fileEdit: "fileEdit",
};

function exposureName(flag: string): { key: string; vars: Record<string, string> } {
  if (flag.startsWith("file:")) return { key: "file", vars: { name: flag.slice(5) } };
  return { key: EXPOSURE_KEYS[flag] ?? "other", vars: { name: flag } };
}

/**
 * The diff as sentences, alarms first. Every key lives under
 * `monitoring.reporter.diff.` and every line carries what its sentence needs.
 * `fmtDate` writes a time for a sentence (the screen passes its date format;
 * the default leaves the ISO time as it is).
 */
export function diffLines(diff: ReportDiffView, fmtDate: (iso: string) => string = (iso) => iso): DiffLine[] {
  const out: DiffLine[] = [];
  const add = (key: string, vars: Record<string, string | number> = {}, alarm = false) =>
    out.push({ key: `monitoring.reporter.diff.${key}`, vars, alarm });

  for (const login of diff.newAdmins) add("newAdmin", { login }, true);
  for (const f of diff.filesAdded) add(`fileAdded.${f.kind}`, { path: f.path }, true);
  for (const flag of diff.exposureRaised) {
    const e = exposureName(flag);
    add(`exposureRaised.${e.key}`, e.vars, true);
  }
  // A must-use plugin loads on every request and cannot be switched off in
  // wp-admin: a new one is rarely ours and a favourite hiding place.
  for (const m of diff.muPlugins.added) add("muAdded", { file: m }, true);

  if (diff.core) add("core", { ...diff.core });
  if (diff.php) add("php", { ...diff.php });
  for (const kind of ["plugins", "themes"] as const) {
    const c = diff[kind];
    const one = kind === "plugins" ? "plugin" : "theme";
    for (const i of c.added) add(`${one}Added`, { name: i.name || i.id, version: i.version });
    for (const i of c.updated) add(`${one}Updated`, { name: i.name || i.id, from: i.from, to: i.to });
    for (const i of c.removed) add(`${one}Removed`, { name: i.name || i.id });
    for (const id of c.activated) add(`${one}Activated`, { name: id });
    for (const id of c.deactivated) add(`${one}Deactivated`, { name: id });
  }
  // Item J: whether the update had a fresh backup before it. None at all, or
  // only old ones, is an alarm: the update went in without a way back.
  const before = diff.backupBefore;
  if (before) {
    const verdict = backupVerdict(before.verdict);
    // The newest backup that existed before the change.
    const newest = [before.dbAt, before.filesAt].filter((x): x is string => Boolean(x)).sort().pop() ?? null;
    const key = verdict === "stale" && !newest ? "staleUndated" : verdict;
    add(`backupBefore.${key}`, newest && verdict !== "none" && verdict !== "unknown" ? { date: fmtDate(newest) } : {}, verdictAlarm(verdict));
  }
  for (const m of diff.muPlugins.removed) add("muRemoved", { file: m });
  if (diff.adminCount) add("adminCount", { ...diff.adminCount });
  for (const f of diff.filesCleared) add(`fileCleared.${f.kind}`, { path: f.path });
  for (const flag of diff.exposureCleared) {
    const e = exposureName(flag);
    add(`exposureCleared.${e.key}`, e.vars);
  }
  // Stable: alarms first, each group in the order above.
  return [...out.filter((l) => l.alarm), ...out.filter((l) => !l.alarm)];
}
